import { useLayoutEffect, useRef } from 'react';

export const useDropdownHandlers = (callback: (open: boolean) => void, enabled: boolean) => {
	const fnRef = useRef(callback);
	const containerRef = useRef<HTMLButtonElement>(null);
	const popupRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		fnRef.current = callback;
	}, [callback]);

	useLayoutEffect(() => {
		if (!enabled) return;

		const button = containerRef.current;
		const popup = popupRef.current;
		if (!button || !popup) return;

		const handlePointerDown = (e: MouseEvent | TouchEvent) => {
			if (!popup || !button) return;

			const target = e.target as Node;
			if (!popup.contains(target) && !button.contains(target)) {
				fnRef.current(false);
			}
		};

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				fnRef.current(false);
			}
		};

		const updatePosition = () => {
			if (!button || !popup) return;

			const buttonRect = button.getBoundingClientRect();
			const popupRect = popup.getBoundingClientRect();
			const spaceBelow = window.innerHeight - buttonRect.bottom;
			const spaceAbove = buttonRect.top;

			let top: number;
			if (spaceBelow >= popupRect.height || spaceBelow >= spaceAbove) {
				top = buttonRect.bottom;
			} else {
				top = buttonRect.top - popupRect.height;
			}

			let left = buttonRect.left;
			if (left + popupRect.width > window.innerWidth) {
				left = window.innerWidth - popupRect.width;
			}
			if (left < 0) left = 0;

			popup.style.width = `${buttonRect.width}px`;
			popup.style.position = 'fixed'; // changed to fixed
			popup.style.top = `${top}px`;
			popup.style.left = `${left}px`;
			popup.style.zIndex = '1000';
		};

		requestAnimationFrame(updatePosition);
		const timer = setTimeout(updatePosition, 0);

		window.addEventListener('scroll', updatePosition, true);
		window.addEventListener('resize', updatePosition);
		document.addEventListener('mousedown', handlePointerDown, { capture: true });
		document.addEventListener('touchstart', handlePointerDown, { capture: true });
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
			document.removeEventListener('mousedown', handlePointerDown, { capture: true });
			document.removeEventListener('touchstart', handlePointerDown, { capture: true });
			document.removeEventListener('keydown', handleKeyDown);

			clearTimeout(timer);
		};
	}, [enabled]);

	return { containerRef, popupRef };
};
